import { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, Loader2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Prerequisites checklist for bulk import
const PREREQUISITES = {
  students: [
    {
      title: "Create Classes",
      description: "Create the classes where students will be admitted",
      path: "/classes",
      required: true
    },
    {
      title: "Create Sections (Optional)",
      description: "If you use sections, create them first",
      path: "/classes",
      required: false
    },
    {
      title: "Create Fee Heads",
      description: "Create fee heads like Re-Admission Fee, Tuition Fee, Lab Fee etc.",
      path: "/fee-management",
      required: true
    },
    {
      title: "Setup Fee Structure",
      description: "Configure fee structure for each class",
      path: "/fee-management",
      required: true
    },
    {
      title: "Set Academic Year/Session",
      description: "Ensure current academic session is set correctly",
      path: "/academic",
      required: true
    }
  ],
  teachers: [
    {
      title: "Create Subjects",
      description: "Create subjects that teachers will teach",
      path: "/classes",
      required: false
    }
  ]
};

// Important notes for bulk import
const IMPORT_NOTES = {
  students: [
    "All students will be imported as RE-ADMISSION (not New Admission)",
    "Re-Admission Fee + Yearly Fees + Monthly Tuition will be auto-assigned",
    "Admission Fee will NOT be assigned",
    "Hostel/Transport Fees will NOT be assigned - allocate separately",
    "class_name in CSV must match exactly (e.g., Class 1, Class 2)"
  ],
  teachers: [
    "Basic teacher info will be imported",
    "Email must be unique"
  ]
};

const BulkImportDialog = ({ open, onOpenChange, type, onSuccess }) => {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [template, setTemplate] = useState(null);
  const [showPrerequisites, setShowPrerequisites] = useState(true);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bulk/templates/${type}`);
      setTemplate(response.data);
    } catch (error) {
      toast.error('Failed to fetch template');
    }
  };

  const downloadTemplate = () => {
    if (!template) return;
    
    const csvContent = [
      template.headers.join(','),
      template.sample_row.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_import_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const parseCSV = (text) => {
    try {
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('CSV must have headers and at least one data row');
      }
      
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const data = [];
      const parseErrors = [];
      
      for (let i = 1; i < lines.length; i++) {
        try {
          // Handle CSV values with commas inside quotes
          const values = [];
          let current = '';
          let inQuotes = false;
          
          for (const char of lines[i]) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim());
          
          if (values.length !== headers.length) {
            parseErrors.push(`Row ${i + 1}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
            continue;
          }
          
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx] || null;
          });
          data.push(row);
        } catch (rowError) {
          parseErrors.push(`Row ${i + 1}: ${rowError.message}`);
        }
      }
      
      if (parseErrors.length > 0 && data.length === 0) {
        throw new Error(`CSV parsing failed: ${parseErrors.slice(0, 3).join('; ')}`);
      }
      
      return { data, parseErrors };
    } catch (error) {
      throw new Error(`CSV parsing error: ${error.message}`);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setLoading(true);
    setProgress(10);
    setResults(null);
    
    try {
      // Read file
      const text = await file.text();
      setProgress(30);
      
      // Parse CSV with improved parser
      const { data, parseErrors } = parseCSV(text);
      if (data.length === 0) {
        throw new Error('No valid data rows found in CSV');
      }
      
      // Show parse warnings if any
      if (parseErrors.length > 0) {
        toast.warning(`${parseErrors.length} rows had issues and were skipped`);
      }
      
      setProgress(50);
      
      // Chunk large imports (more than 100 rows)
      const CHUNK_SIZE = 100;
      const chunks = [];
      for (let i = 0; i < data.length; i += CHUNK_SIZE) {
        chunks.push(data.slice(i, i + CHUNK_SIZE));
      }
      
      const endpoint = type === 'students' ? '/api/bulk/students' : '/api/bulk/teachers';
      let totalSuccess = 0;
      let totalFailed = 0;
      let allErrors = [];
      
      // Process chunks
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const payload = type === 'students' ? { students: chunk } : { teachers: chunk };
        
        try {
          const response = await axios.post(`${API_URL}${endpoint}`, payload, {
            headers: getAuthHeader(),
            timeout: 60000 // 60 second timeout per chunk
          });
          
          totalSuccess += response.data.success || 0;
          totalFailed += response.data.failed || 0;
          if (response.data.errors) {
            allErrors = [...allErrors, ...response.data.errors];
          }
        } catch (chunkError) {
          totalFailed += chunk.length;
          allErrors.push({ error: `Chunk ${i + 1} failed: ${chunkError.message}` });
        }
        
        // Update progress
        setProgress(50 + Math.round((i + 1) / chunks.length * 45));
      }
      
      setProgress(100);
      setResults({
        success: totalSuccess,
        failed: totalFailed,
        errors: allErrors,
        parseErrors: parseErrors
      });
      
      if (totalSuccess > 0) {
        toast.success(`Successfully imported ${totalSuccess} ${type}`);
        if (onSuccess) onSuccess();
      }
      
      if (totalFailed > 0) {
        toast.error(`${totalFailed} ${type} failed to import`);
      }
      
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Import failed';
      toast.error(errorMessage);
      setResults({ 
        success: 0, 
        failed: 0, 
        errors: [{ error: errorMessage }],
        parseErrors: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setProgress(0);
    onOpenChange(false);
  };

  // Fetch template when dialog opens
  if (open && !template) {
    fetchTemplate();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Bulk Import {type === 'students' ? 'Students (Re-Admission)' : 'Teachers'}
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple {type} at once
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Prerequisites Checklist */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => setShowPrerequisites(!showPrerequisites)}
              className="w-full flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="font-medium text-sm text-amber-800 dark:text-amber-200">
                  Prerequisites - Complete Before Import
                </span>
              </div>
              {showPrerequisites ? (
                <ChevronUp className="h-4 w-4 text-amber-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-amber-600" />
              )}
            </button>
            
            {showPrerequisites && (
              <div className="p-3 space-y-2 bg-amber-50/50 dark:bg-amber-950/20">
                {PREREQUISITES[type]?.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                      item.required ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 dark:text-gray-200">
                        {item.title}
                        {item.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Important Notes */}
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-sm text-blue-800 dark:text-blue-200">Important Information</span>
            </div>
            <ul className="space-y-1">
              {IMPORT_NOTES[type]?.map((note, idx) => (
                <li key={idx} className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1">
                  <span className="mt-0.5">{idx < 2 ? '✓' : idx < 4 ? '✗' : '•'}</span>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Template Download */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Download Template</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use this template to format your data correctly
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} disabled={!template}>
                <Download className="h-4 w-4 mr-2" />
                Template
              </Button>
            </div>
            {template && (
              <p className="text-xs text-muted-foreground mt-2 border-t pt-2">
                {template.notes}
              </p>
            )}
          </div>

          {/* File Upload */}
          <div 
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${file ? 'border-green-500 bg-green-50' : 'border-muted-foreground/25 hover:border-primary'}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">{file.name}</span>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to select or drag a CSV file
                </p>
              </div>
            )}
          </div>

          {/* Progress */}
          {loading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Importing...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg text-center">
                  <CheckCircle2 className="h-5 w-5 mx-auto text-green-600 mb-1" />
                  <p className="text-xl font-bold text-green-600">{results.success}</p>
                  <p className="text-xs text-green-700 dark:text-green-400">Imported</p>
                </div>
                <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded-lg text-center">
                  <XCircle className="h-5 w-5 mx-auto text-red-600 mb-1" />
                  <p className="text-xl font-bold text-red-600">{results.failed}</p>
                  <p className="text-xs text-red-700 dark:text-red-400">Failed</p>
                </div>
              </div>

              {/* Fees Summary for Students */}
              {type === 'students' && results.fees_summary && results.fees_summary.total_invoices_generated > 0 && (
                <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                  <p className="font-medium text-sm text-purple-800 dark:text-purple-200 mb-2">💰 Auto Fee Summary</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-purple-600">{results.fees_summary.total_students_with_fees}</p>
                      <p className="text-xs text-purple-700 dark:text-purple-400">Students</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-600">{results.fees_summary.total_invoices_generated}</p>
                      <p className="text-xs text-purple-700 dark:text-purple-400">Invoices</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-600">₹{results.fees_summary.total_amount?.toLocaleString()}</p>
                      <p className="text-xs text-purple-700 dark:text-purple-400">Total Amount</p>
                    </div>
                  </div>
                </div>
              )}

              {/* No fees warning */}
              {type === 'students' && results.success > 0 && (!results.fees_summary || results.fees_summary.total_invoices_generated === 0) && (
                <Alert className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-300">
                    <p className="font-medium">No Fee Invoices Generated!</p>
                    <p className="text-xs mt-1">
                      Possible reason: Fee Structure not configured for the class. 
                      Go to Fee Management → Fee Structure to set it up.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              
              {results.errors && results.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium mb-1">Import Errors:</p>
                    <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                      {results.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>
                          {err.row ? `Row ${err.row}: ${err.name} - ` : ''}{err.error}
                        </li>
                      ))}
                      {results.errors.length > 10 && (
                        <li>...and {results.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              
              {results.parseErrors && results.parseErrors.length > 0 && (
                <Alert className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-300">
                    <p className="font-medium mb-1">CSV Parsing Warnings:</p>
                    <ul className="text-xs space-y-1 max-h-24 overflow-y-auto">
                      {results.parseErrors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {results.parseErrors.length > 5 && (
                        <li>...and {results.parseErrors.length - 5} more warnings</li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose}>
              {results ? 'Close' : 'Cancel'}
            </Button>
            {!results && (
              <Button onClick={handleImport} disabled={!file || loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import {type === 'students' ? 'Students' : 'Teachers'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportDialog;
